import { canManageTeam, MAX_TEAM_MEMBERS, type TeamDetail, type TeamMember } from "@69ly/shared";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, NavLink, useLocation } from "react-router";
import { Avatar } from "../components/Avatar.tsx";
import { Icon } from "../components/Icon.tsx";
import { Banner, ErrorState, Loading } from "../components/Status.tsx";
import { useCurrentTeam } from "../lib/current-team.tsx";
import { errorText } from "../lib/errors.ts";
import { useMe, useRemoveMember, useRotateInvite, useSetRole, useTeam } from "../lib/queries.ts";
import { WhoKnowsWhat } from "../components/WhoKnowsWhat.tsx";
import { TeamPositions } from "../components/TeamPositions.tsx";
import { TeamMapBar, useTeamMap } from "../components/TeamMapBar.tsx";

export const formatDate = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

export function InviteCard({ team }: { team: TeamDetail }) {
  const { t } = useTranslation();
  const rotate = useRotateInvite(team.id);
  const [copied, setCopied] = useState(false);
  const link = team.invite ? `${window.location.origin}/join/${team.invite.code}` : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard refused (insecure page or permissions): the link stays selectable.
    }
  };

  return (
    <section className="card" aria-labelledby="invite-title">
      <h2 id="invite-title" className="eyebrow">
        {t("team.inviteTitle")}
      </h2>
      <p className="muted small">{t("team.inviteHelp")}</p>
      {team.invite ? (
        <>
          <div className="copy-row">
            <input className="mono" readOnly value={link} aria-label={t("team.inviteTitle")} onFocus={(e) => e.currentTarget.select()} />
            <button type="button" className="btn btn-secondary" onClick={copy}>
              <Icon name={copied ? "check" : "copy"} />
              {copied ? t("team.copied") : t("team.copy")}
            </button>
          </div>
          <p className="muted small">{t("team.inviteExpires", { date: formatDate(team.invite.expiresAt) })}</p>
        </>
      ) : (
        <p className="muted">{t("team.noInvite")}</p>
      )}
      <div className="row">
        <button type="button" className="btn btn-quiet" onClick={() => rotate.mutate()} disabled={rotate.isPending}>
          {t("team.newLink")}
        </button>
        <span className="muted small">{t("team.newLinkHelp")}</span>
      </div>
      {rotate.error && <p className="field-error">{errorText(t, rotate.error)}</p>}
    </section>
  );
}

function MemberActions({ team, member, meId }: { team: TeamDetail; member: TeamMember; meId: string }) {
  const { t } = useTranslation();
  const setRole = useSetRole(team.id);
  const remove = useRemoveMember(team.id);
  if (member.userId === meId) return null;
  const isOwner = team.myRole === "owner";
  const canRemove = isOwner || (team.myRole === "captain" && member.role === "member");
  const busy = setRole.isPending || remove.isPending;

  return (
    <div className="member-actions">
      {isOwner && member.role === "member" && (
        <button type="button" className="btn btn-quiet" disabled={busy} onClick={() => setRole.mutate({ userId: member.userId, role: "captain" })}>
          {t("team.makeCaptain")}
        </button>
      )}
      {isOwner && member.role === "captain" && (
        <button type="button" className="btn btn-quiet" disabled={busy} onClick={() => setRole.mutate({ userId: member.userId, role: "member" })}>
          {t("team.makeMember")}
        </button>
      )}
      {isOwner && (
        <button
          type="button"
          className="btn btn-quiet"
          disabled={busy}
          onClick={() => {
            if (window.confirm(t("team.confirmOwner", { name: member.name }))) setRole.mutate({ userId: member.userId, role: "owner" });
          }}
        >
          {t("team.makeOwner")}
        </button>
      )}
      {canRemove && member.role !== "owner" && (
        <button
          type="button"
          className="btn btn-quiet btn-danger"
          disabled={busy}
          onClick={() => {
            if (window.confirm(t("team.confirmRemove", { name: member.name }))) remove.mutate(member.userId);
          }}
        >
          {t("team.remove")}
        </button>
      )}
      {(setRole.error || remove.error) && <span className="field-error">{errorText(t, setRole.error ?? remove.error)}</span>}
    </div>
  );
}

const TABS = ["players", "positions", "knowledge"] as const;
export type TeamTab = (typeof TABS)[number];
const TAB_PATH: Record<TeamTab, string> = { players: "/team", positions: "/team/positions", knowledge: "/team/knowledge" };

/**
 * The Team page (0.23.0): three tabs, each in the address. What is about
 * managing the team (invites, FACEIT, Discord, leaving) lives in Settings.
 */
function TeamView({ team, tab }: { team: TeamDetail; tab: TeamTab }) {
  const { t } = useTranslation();
  const me = useMe().data?.user;
  const location = useLocation();
  const joined = (location.state as { joined?: string } | null)?.joined;
  const manager = canManageTeam(team.myRole);
  const [map, setMap] = useTeamMap();

  return (
    <div className="page team-page">
      {joined && <Banner tone="success">{t("join.joined", { team: joined })}</Banner>}
      <div className="page-head">
        <div>
          <p className="eyebrow">{t("team.eyebrow")}</p>
          <h1 className="page-title">{team.name}</h1>
        </div>
        <span className="count-pill">{t("team.count", { count: team.members.length })}</span>
      </div>
      <nav className="page-tabs" aria-label={t("team.tabs")}>
        {TABS.map((key) => (
          <NavLink key={key} to={TAB_PATH[key]} end className={({ isActive }) => `page-tab${isActive ? " is-active" : ""}`}>
            {t(`team.tab.${key}`)}
          </NavLink>
        ))}
      </nav>

      {tab === "players" && (
        <section className="card" aria-labelledby="members-title">
          <h2 id="members-title" className="eyebrow">
            {t("team.members")}
          </h2>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t("team.player")}</th>
                  <th scope="col">{t("team.role")}</th>
                  <th scope="col">{t("team.joined")}</th>
                  <th scope="col">
                    <span className="visually-hidden">{t("team.actions")}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {team.members.map((m) => {
                  const mine = m.userId === me?.id;
                  return (
                    <tr key={m.userId} className={mine ? "is-me" : undefined}>
                      <td>
                        <span className="player-cell">
                          <Avatar name={m.name} url={m.avatarUrl} me={mine} />
                          <span>
                            {m.name}
                            {mine && <span className="muted"> ({t("team.you")})</span>}
                          </span>
                        </span>
                      </td>
                      <td>
                        <span className={`role-tag role-${m.role}`}>{t(`team.roles.${m.role}`)}</span>
                      </td>
                      <td className="muted">{formatDate(m.joinedAt)}</td>
                      <td>{me && <MemberActions team={team} member={m} meId={me.id} />}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="team-players-foot">
            <span className="muted small">{manager ? t("team.managedInSettings") : t("team.inviteOnlyManagers")}</span>
            {manager && team.members.length < MAX_TEAM_MEMBERS && (
              <Link to="/settings/team" className="btn btn-secondary btn-small">
                <Icon name="link" size={14} />
                {t("team.inviteButton")}
              </Link>
            )}
          </div>
        </section>
      )}
      {tab !== "players" && <TeamMapBar map={map} onPick={setMap} />}
      {tab === "positions" && <TeamPositions team={team} meId={me?.id} map={map} />}
      {tab === "knowledge" && <WhoKnowsWhat team={team} map={map} />}
    </div>
  );
}

export function TeamPage({ tab = "players" }: { tab?: TeamTab }) {
  const { t } = useTranslation();
  const { team } = useCurrentTeam();
  const detail = useTeam(team?.id);
  const location = useLocation();
  // The Discord card moved to Settings › Team (0.23.0): old links to /team#discord still land on it.
  if (location.hash === "#discord") return <Navigate to="/settings/team#discord" replace />;

  if (!team) {
    return (
      <div className="page page-narrow">
        <div className="card">
          <h1 className="section-title">{t("team.noTeamTitle")}</h1>
          <p className="muted">{t("team.noTeamIntro")}</p>
          <Link to="/setup" className="btn btn-primary">
            {t("team.setUp")}
          </Link>
        </div>
      </div>
    );
  }
  if (detail.isPending) return <Loading />;
  if (detail.error) return <ErrorState message={errorText(t, detail.error)} onRetry={() => void detail.refetch()} />;
  return <TeamView team={detail.data} tab={tab} />;
}
